import type { HardwareBridge } from "./hardware";

declare global {
  interface Window {
    /** Injected by the Electron preload (Phase 5). Absent in a normal browser. */
    hardware?: HardwareBridge;
    /** Optional runtime config injected by the Electron shell. */
    __ASTRO__?: { apiUrl?: string; terminalId?: string };
  }
}

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

/** API base URL: runtime override → Electron config → build-time env → same-origin proxy. */
export function apiBase(): string {
  const configured = read("astro.apiUrl") ?? window.__ASTRO__?.apiUrl ?? import.meta.env.VITE_API_URL ?? "/api/v2";
  // an address without a version ("/api" or "https://host/api") follows whichever generation the server reports (see detectApi)
  return /\/api\/?$/.test(configured) && detected ? `${configured.replace(/\/$/, "")}/${detected}` : configured;
}

let detected: "v1" | "v2" | null = null;

/**
 * Cutover without touching a single terminal: configure the address as `…/api`; the server answers `/api/info` with the
 * generation that is live ("v1" or "v2") and the app follows. Called once at start-up; failures fall back to v2 (the current generation).
 */
export async function detectApi(): Promise<void> {
  const configured = read("astro.apiUrl") ?? window.__ASTRO__?.apiUrl ?? import.meta.env.VITE_API_URL ?? "/api/v2";
  if (!/\/api\/?$/.test(configured)) return;
  try {
    const r = await fetch(`${configured.replace(/\/$/, "")}/info`, { headers: { accept: "application/json" } });
    const j = (await r.json()) as { api?: string };
    detected = j.api === "v1" ? "v1" : "v2";
  } catch {
    detected = "v2";
  }
}

/** Identifies this POS terminal; drives per-terminal shifts and cash drawers on the server. */
export function terminalId(): string {
  return read("astro.terminalId") ?? window.__ASTRO__?.terminalId ?? (window.hardware ? "POS-1" : "web");
}

/** True when the client talks to the v2 API (tenant login, branch header, feature switches, wallet, waivers, overrides). */
export function isV2(): boolean {
  return /\/v2(\/|$)/.test(apiBase());
}
